import LayerStandard from 'Simulations/LayerStandard';
import { AbstractSerializer } from '../../../common/AbstractSerializer';
import type { BinaryReader } from '../../../common/BinaryReader';
import Header from '../../../common/Header.js';
import { initWorker } from '../../../common/WorkerUtility.js';
import type { LayerRecord, SerializerResolver } from '@visualizer/types';

export default class RttSerializer extends AbstractSerializer<void> {
    public readonly version: string;
    private readonly fileVersion: string;

    constructor(version: string) {
        super();
        this.version = version;
        this.fileVersion = version;
    }

    public async read(reader: BinaryReader, layer: LayerStandard & LayerRecord, sm: SerializerResolver): Promise<void> {
        const worker = await initWorker()!;

        return new Promise((resolve, reject) => {
            worker.onmessage = (e: any) => {
                if (e.data.rays != null) {
                    // layer.raysHandler.init(e.data.rays, layer, 0);
                }

                if (e.data.paramsStatus != null) {
                    // layer.raysHandler.addParams(e.data.paramsStatus, e.data.params1, e.data.params2);
                }

                if (e.data.spID != null) {
                    // const { frameID, spID, values, indices, isContinue } = e.data;
                    // layer.raysHandler.addRays(frameID, spID, indices, values, isContinue);
                }
                if (e.data.finished === true) {
                    console.log('Rays have been loaded in all the Frames and ready to use', layer.raysHandler);
                    cleanup();
                    resolve();
                }
                if (e.data?.error) {
                    cleanup();
                    reject(new Error(e.data.error || 'Worker error'));
                }
            };

            worker.onerror = (e: ErrorEvent) => {
                cleanup();
                reject(e.error || new Error(e.message || 'Worker script error'));
            };

            function cleanup() {
                try {
                    worker.terminate();
                } catch {}
            }

            const data = reader.getData();
            const headerSerializer = sm.findSerializer(this.fileVersion, Header, 'binary');
            worker.postMessage({ data, kind: 'rtt', serializerVersion: headerSerializer.serializerVersion }, [data]);
        });
    }
}

export function loadRTT(generalLoader: BinaryReader, sensorPointsN: number, postMessage: (payload: Record<string, unknown>) => void): void {
    generalLoader.readUInt32();

    const numFrames = generalLoader.readUInt8();
    const numRays = generalLoader.readUInt32();

    const rays = new Array(numRays);

    for (let r = 0; r < numRays; r++) {
        const ray = generalLoader.readVec3();
        rays[r] = ray;
    }
    postMessage({ rays });

    const hasParams = generalLoader.readUInt8();

    if (hasParams > 0) {
        const params1: number[] = [];
        const params2: number[] = [];
        for (let r = 0; r < numRays; r++) {
            params1[r] = generalLoader.readFloat32();
            params2[r] = generalLoader.readFloat32();
        }

        postMessage({
            paramsStatus: 'done',
            params1,
            params2,
        });
    } else {
        postMessage({ paramsStatus: 'compute' });
    }

    for (let f = 0; f < numFrames; f++) {
        generalLoader.readString();
        const isEqual = generalLoader.readBoolean();
        let R1 = 0;
        let indices: number[] = [];
        for (let N = 0; N < sensorPointsN; N++) {
            if (!isEqual || N === 0) {
                R1 = generalLoader.readUInt32();
            }
            const values: number[] = [];
            for (let iL = 0; iL < R1; iL++) {
                values[iL] = generalLoader.readFloat32();
            }

            if (isEqual && N > 0) {
                const isContinue = indices.length === 1 && R1 !== 1;

                postMessage({ frameID: f, spID: N, R1, indices, values, isContinue });
                continue;
            }
            // From here and below, if is equal is true, this is not computed

            const isContinue = generalLoader.readBoolean();
            indices = [];
            if (isContinue) {
                const a = generalLoader.readUInt32();
                indices = [a];
            } else {
                for (let iL = 0; iL < R1; iL++) {
                    indices[iL] = generalLoader.readUInt32();
                }
            }

            postMessage({ frameID: f, spID: N, R1, indices, values, isContinue });
        }
    }
}
